import { AnalyticsBrowser } from "@segment/analytics-next";
import type { RouteLocationNormalized } from "vue-router";
export type AnalyticsTrackingPlan = Record<string, unknown | undefined>;
import type { App, Directive } from "vue";

export type AnalyticsClient<P extends AnalyticsTrackingPlan> = {
  track<E extends keyof P, T extends P[E]>(
    ...args: T extends undefined ? [E] : [E, T]
  ): void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  identify(id?: any, payload?: any): void;
};

export type AnalyticsTrackDirective<P extends AnalyticsTrackingPlan> =
  Directive<HTMLElement, keyof P>;

export function getAnalyticsClient<P extends AnalyticsTrackingPlan>(
  apiKey?: string,
  options?: {
    globalContext?: MaybeRef<Record<string, unknown>>;
  },
): AnalyticsClient<P> {
  const { context, onRouteChange } = useAnalyticsContext();

  function getContext() {
    return {
      ...unref(options?.globalContext?.value ?? {}),
      ...context.value,
    };
  }

  const analytics: AnalyticsBrowser = apiKey
    ? AnalyticsBrowser.load({ writeKey: apiKey })
    : ({
        track(...args) {
          // eslint-disable-next-line no-console
          console.info(`👀 Analytics event`, args[0], {
            ...getContext(),
            ...args[1],
          });
        },
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        identify: (id?: any, payload?: any) => {
          // eslint-disable-next-line no-console
          console.info(`👀 Analytics identification`, id, payload);
        },
        page(category: string, path: string, context: unknown) {
          // eslint-disable-next-line no-console
          console.info(`👀 Analytics page view`, path, context);
        },
      } as AnalyticsBrowser);

  const router = useRouter();
  const { me } = useCurrentUser();

  const track = <E extends keyof P, T extends P[E]>(
    ...args: T extends undefined ? [E] : [E, T]
  ) => {
    analytics.track(args[0] as string, {
      email: me.value?.email,
      ...getContext(),
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-expect-error
      ...args[1],
    });
  };

  if (import.meta.client) {
    router.afterEach(
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      (to: RouteLocationNormalized, from: RouteLocationNormalized) => {
        analytics.page("", to.path, {
          path: to.path,
          url: to.fullPath,
          referrer: from.fullPath,
          ...getContext(),
        });
      },
    );

    router.beforeEach(
      (to: RouteLocationNormalized, from: RouteLocationNormalized) => {
        if (to.path !== from.path) {
          onRouteChange(to, from);
        }
      },
    );
  }

  return {
    identify: analytics.identify,
    track,
  };
}

export function setupAnalyticDirectives<
  P extends AnalyticsTrackingPlan,
>(nuxtApp: { $analytics?: AnalyticsClient<P>; vueApp: App }) {
  nuxtApp.vueApp.directive("track", {
    mounted(el, { value }) {
      function clickHandler() {
        if (!nuxtApp.$analytics) {
          return useMonitoring().logError(
            new Error("Track directive - missing $analytics client"),
          );
        }

        if (typeof value === "object") {
          return nuxtApp.$analytics.track(value.event, value.data);
        }

        // @ts-expect-error Analytics tracking
        return nuxtApp.$analytics.track(value);
      }

      if (el.destroyAttachedDirective) return;

      function destroyAttachedDirective() {
        el.removeEventListener("click", clickHandler);
      }

      el.addEventListener("click", clickHandler);
      Object.defineProperty(el, "destroyAttachedDirective", {
        value: destroyAttachedDirective,
      });
    },
    unmounted(el) {
      el.destroyAttachedDirective?.();
    },
  });
}
